import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Grid,
  Paper,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as BackIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getBlogPostById } from '../../services/blogService';

const BlogPostPreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await getBlogPostById(id);
        if (response.success && response.data) {
          setPost(response.data);
        } else {
          setError('Failed to load blog post');
        }
      } catch (error) {
        console.error('Error fetching blog post:', error);
        setError('Failed to load blog post');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [id]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error || !post) {
    return (
      <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            {error || 'Blog post not found'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/blog')}
            sx={{ mt: 2 }}
          >
            Back to Blog Posts
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ pt: 4, pb: 4 }}>
      {/* Header with actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Button
          variant="outlined"
          startIcon={<BackIcon />}
          onClick={() => navigate('/blog')}
        >
          Back to List
        </Button>
        <Box>
          <Button
            variant="contained"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/blog/edit/${id}`)}
            sx={{ ml: 2 }}
          >
            Edit Post
          </Button>
        </Box>
      </Box>

      {/* Preview notice */}
      <Alert severity={post.published ? 'success' : 'warning'} sx={{ mb: 3 }}>
        {post.published
          ? 'This post is currently published and visible to the public.'
          : 'This post is currently in draft mode and not visible to the public.'}
      </Alert>

      {/* Content preview */}
      <Paper sx={{ overflow: 'hidden' }}>
        {/* Featured badge */}
        {post.featured && (
          <Box
            sx={{
              backgroundImage: (theme) => theme.custom.gradients.brand,
              color: '#fff',
              p: 1,
              pl: 2,
            }}
          >
            <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 600 }}>
              <StarIcon fontSize="small" sx={{ mr: 0.5 }} />
              Featured Post
            </Typography>
          </Box>
        )}
        
        {/* Post header */}
        <Box sx={{ p: 4, pb: 2 }}>
          <Typography variant="h3" component="h1" gutterBottom>
            {post.title}
          </Typography>
          
          {/* Metadata */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
              <Typography variant="body2" color="text.secondary">
                {post.createdAt && format(new Date(post.createdAt), 'MMMM d, yyyy')}
              </Typography>
            </Grid>
            
            {post.author && (
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {post.author}
                </Typography>
              </Grid>
            )}
            
            {post.category && (
              <Grid item sx={{ display: 'flex', alignItems: 'center' }}>
                <CategoryIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {post.category}
                </Typography>
              </Grid>
            )}
          </Grid>
          
          {/* Tags */}
          {post.tags && post.tags.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {post.tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  size="small"
                  sx={{ mr: 0.5, mb: 0.5 }}
                />
              ))}
            </Box>
          )}
        </Box>
        
        <Divider />
        
        {/* Summary */}
        {post.summary && (
          <Box sx={{ p: { xs: 2.5, md: 4 }, backgroundColor: 'action.hover' }}>
            <Typography variant="subtitle1" sx={{ fontStyle: 'italic' }}>
              {post.summary}
            </Typography>
          </Box>
        )}

        {/* Content */}
        <Box sx={{ p: { xs: 2.5, md: 4 } }}>
          <div
            dangerouslySetInnerHTML={{ __html: post.content }}
            style={{ maxWidth: '100%', overflow: 'hidden' }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default BlogPostPreview;